import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { showToast } from '../../utils/toast';
import { resolveTradeUnitPrice, resolveCasePrice, getCaseQuantity, type PricingTier } from '../../utils/wholesaleProduct';
import { evaluateBillingContext, type OrderBillingContext } from '../../utils/orderInvoiceRules';
import OrderBillingBanner from '../../components/OrderBillingBanner';

/* ─── Types ─────────────────────────────────────────── */
type Customer = {
  id: string; name: string; email: string; phone: string;
  address: string; city: string; postcode: string; country: string;
  companyName: string; credit_terms: number; credit_balance: number;
  credit_limit?: number;
  customer_type: string;
  pricing_tier?: PricingTier | string | null;
};
type Product = {
  id: string; name: string; price: number;
  wholesale_price?: number; tier_a_price?: number; tier_b_price?: number; tier_c_price?: number;
  case_price?: number; case_quantity?: number;
  box_quantity?: number; min_order_qty?: number; stock: number; unit: string;
};

type OrderItem = {
  _key: string; productId: string; productName: string;
  description: string; unit: string; qty: number; price: number; discount: number; stock: number;
  saleType: 'unit' | 'box';
  box_quantity?: number;
};

/* ─── Helpers ────────────────────────────────────────── */
const newItem = (): OrderItem => ({
  _key: `k-${Date.now()}-${Math.random()}`, productId: '', productName: '',
  description: '', unit: 'pcs', qty: 1, price: 0, discount: 0, stock: -1,
  saleType: 'unit',
});

const genOrderNumber = () => {
  const year = new Date().getFullYear();
  const rand = String(Math.floor(1000 + Math.random() * 9000));
  return `ORD-${year}-${rand}`;
};

const toN = (v: any, fb = 0) => { const n = Number(v); return isFinite(n) ? n : fb; };

/* ─── Constants ──────────────────────────────────────── */
const ORDER_STATUSES = ['Draft', 'Confirmed', 'Processing', 'Shipped', 'Completed', 'Cancelled'] as const;
const FULFILLMENT_STATUSES = ['Unfulfilled', 'Partially Shipped', 'Shipped'] as const;
const ORDER_SOURCES = ['Online', 'In-store', 'Phone', 'WhatsApp', 'Email', 'Other'];

const STATUS_COLORS: Record<string, string> = {
  Draft: '#6b7280', Confirmed: '#3b82f6', Processing: '#f59e0b',
  Shipped: '#8b5cf6', Completed: '#10b981', Cancelled: '#ef4444',
};
const FULFIL_COLORS: Record<string, string> = {
  Unfulfilled: '#6b7280', 'Partially Shipped': '#f59e0b', Shipped: '#10b981',
};
const PRIORITY_CONFIG = [
  { value: 'Low', icon: '🟢', color: '#10b981' },
  { value: 'Normal', icon: '🔵', color: '#3b82f6' },
  { value: 'High', icon: '🟡', color: '#f59e0b' },
  { value: 'Urgent', icon: '🔴', color: '#ef4444' },
];

/* ══════════════════════════════════════════════════════ */
const OrderForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { token } = useAuth();

  const [pageLoading, setPageLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);

  /* lookups */
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  /* customer section */
  const [isGuest, setIsGuest] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [fetchingCustomer, setFetchingCustomer] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditLimit, setCreditLimit] = useState(0);
  // contract prices: map of productId → agreed price
  const [contractPrices, setContractPrices] = useState<Record<string, number>>({});

  /* items */
  const [items, setItems] = useState<OrderItem[]>([newItem()]);

  /* deposit (shop orders) */
  const [depositAmount, setDepositAmount] = useState(0);
  const [depositConfirmed, setDepositConfirmed] = useState(false);
  const [billingContext, setBillingContext] = useState<OrderBillingContext>(() => evaluateBillingContext(null));

  /* attachments */
  const [attachments, setAttachments] = useState<File[]>([]);

  /* form */
  const [form, setForm] = useState({
    orderNumber: genOrderNumber(),
    orderDate: new Date().toISOString().split('T')[0],
    orderSource: 'Online',
    status: 'Draft' as string,
    fulfillmentStatus: 'Unfulfilled' as string,
    priority: 'Normal',
    billingAddress: '',
    shippingAddress: '',
    sameAsBilling: true,
    customerEmail: '',
    customerPhone: '',
    deliveryMethod: 'Standard',
    deliveryDate: '',
    trackingNumber: '',
    assignedStaff: '',
    internalNotes: '',
    shippingCost: '0',
    taxRate: '20',
    discountType: 'percentage' as 'percentage' | 'fixed',
    discountValue: '0',
    notes: '',
  });

  const f = (name: keyof typeof form) => form[name] as string;

  /* totals */
  const lineSubtotal = items.reduce((s, it) => s + (it.qty * it.price) * (1 - (it.discount || 0) / 100), 0);
  const orderDiscount = form.discountType === 'percentage'
    ? (lineSubtotal * toN(form.discountValue)) / 100
    : toN(form.discountValue);
  const afterDiscount = lineSubtotal - orderDiscount;
  const vat = (afterDiscount * toN(form.taxRate)) / 100;
  const shipping = toN(form.shippingCost);
  const total = afterDiscount + vat + shipping;

  /* ── load all data ── */
  useEffect(() => {
    if (!token) { navigate('/login'); return; }

    const loadLookups = Promise.all([
      apiFetch(`${API_URL}/customers`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
      apiFetch(`${API_URL}/products`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
    ]);

    if (isEdit && id) {
      Promise.all([
        loadLookups,
        apiFetch(`${API_URL}/orders/${id}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
      ]).then(([[custData, prodData], order]) => {
        initLookups(custData, prodData);
        if (order) {
          initOrder(order, Array.isArray(custData) ? custData : []);
          setBillingContext(order.billingContext || evaluateBillingContext(order.linkedInvoice));
        }
      }).finally(() => setPageLoading(false));
    } else {
      loadLookups.then(([custData, prodData]) => initLookups(custData, prodData));
    }
  }, [token, id, isEdit, navigate]);

  const initLookups = (custData: any, prodData: any) => {
    setCustomers((Array.isArray(custData) ? custData : []).map((c: any) => ({
      id: String(c._id || c.id), name: c.name, email: c.email || '',
      phone: c.phone || '', address: c.address || '', city: c.city || '',
      postcode: c.postcode || '', country: c.country || '',
      companyName: c.companyName || c.companyname || '',
      credit_terms: toN(c.credit_terms, 30), credit_balance: toN(c.credit_balance),
      credit_limit: toN(c.credit_limit),
      customer_type: c.customer_type || c.customerType || 'Retail',
      pricing_tier: c.pricing_tier || c.pricingTier || null,
    })));
    const prodArr = Array.isArray(prodData) ? prodData : prodData?.products || [];
    setProducts(prodArr.map((p: any) => ({
      id: String(p._id || p.id), name: p.name,
      price: toN(p.price), stock: toN(p.quantity ?? p.stock ?? 0),
      unit: p.unit || 'pcs',
      wholesale_price: p.wholesale_price != null ? toN(p.wholesale_price) : undefined,
      tier_a_price: p.tier_a_price != null ? toN(p.tier_a_price) : undefined,
      tier_b_price: p.tier_b_price != null ? toN(p.tier_b_price) : undefined,
      tier_c_price: p.tier_c_price != null ? toN(p.tier_c_price) : undefined,
      case_price: p.case_price != null ? toN(p.case_price) : undefined,
      case_quantity: p.case_quantity != null ? toN(p.case_quantity) : undefined,
      box_quantity: p.box_quantity != null ? toN(p.box_quantity) : undefined,
      min_order_qty: p.min_order_qty != null ? toN(p.min_order_qty) : undefined,
    })));
  };

  const initOrder = (order: any, custList: any[]) => {
    const g = (s: string, c: string, fb = '') => order[s] ?? order[c] ?? fb;
    const custId = String(g('customer_id', 'customerId', ''));
    const linked = custList.find((c: any) => String(c._id || c.id) === custId);
    if (linked) {
      setSelectedCustomer({ id: String(linked._id || linked.id), name: linked.name, email: linked.email || '', phone: linked.phone || '', address: linked.address || '', city: linked.city || '', postcode: linked.postcode || '', country: linked.country || '', companyName: linked.companyName || '', credit_terms: toN(linked.credit_terms, 30), credit_balance: toN(linked.credit_balance), credit_limit: toN(linked.credit_limit), customer_type: linked.customer_type || linked.customerType || 'Retail', pricing_tier: linked.pricing_tier || linked.pricingTier || null });
      setSearchTerm(linked.name);
      setCreditBalance(toN(linked.credit_balance));
      setCreditLimit(toN(linked.credit_limit));
    } else if (g('customer_name', 'customerName')) {
      setIsGuest(true);
      setSearchTerm(g('customer_name', 'customerName'));
    }

    const billing = g('billing_address', 'billingAddress');
    const ship = g('shipping_address', 'shippingAddress');

    setForm(prev => ({
      ...prev,
      orderNumber: g('order_number', 'orderNumber', prev.orderNumber),
      orderDate: order.order_date ? order.order_date.split('T')[0] : (order.orderDate ? order.orderDate.split('T')[0] : prev.orderDate),
      orderSource: g('order_source', 'orderSource', 'Online'),
      status: order.status || 'Draft',
      fulfillmentStatus: g('fulfillment_status', 'fulfillmentStatus', 'Unfulfilled'),
      priority: order.priority || 'Normal',
      billingAddress: billing,
      shippingAddress: ship || billing,
      sameAsBilling: !ship || ship === billing,
      customerEmail: g('customer_email', 'customerEmail'),
      customerPhone: g('customer_phone', 'customerPhone'),
      deliveryMethod: g('delivery_method', 'deliveryMethod', 'Standard'),
      deliveryDate: order.delivery_date ? order.delivery_date.split('T')[0] : (order.deliveryDate ? order.deliveryDate.split('T')[0] : ''),
      trackingNumber: g('tracking_number', 'trackingNumber'),
      assignedStaff: g('assigned_staff', 'assignedStaff'),
      internalNotes: g('internal_notes', 'internalNotes'),
      shippingCost: String(order.shipping_cost ?? order.shippingCost ?? 0),
      taxRate: String(order.tax_rate ?? order.taxRate ?? 20),
      discountType: g('discount_type', 'discountType', 'percentage') as 'percentage' | 'fixed',
      discountValue: String(order.discount_value ?? order.discountValue ?? 0),
      notes: order.notes || order.description || '',
    }));

    if (Array.isArray(order.items) && order.items.length > 0) {
      setItems(order.items.map((it: any, i: number) => ({
        _key: `loaded-${i}-${Math.random()}`,
        productId: String(it.product_id || it.productId || ''),
        productName: it.product_name || it.productName || it.name || '',
        description: it.description || '',
        unit: it.unit || 'pcs',
        qty: toN(it.qty ?? it.quantity, 1),
        price: toN(it.price ?? it.unit_price ?? it.unitPrice, 0),
        discount: toN(it.discount_pct ?? it.discountPct ?? it.discount, 0),
        stock: -1,
        saleType: (it.sale_type || it.saleType || 'unit') as 'unit' | 'box',
        box_quantity: it.box_quantity != null ? toN(it.box_quantity) : undefined,
      })));
    }

    setDepositAmount(toN(order.deposit_amount ?? order.depositAmount, 0));
    setDepositConfirmed(Boolean(order.deposit_confirmed ?? order.depositConfirmed));
  };

  /* ── customer ── */
  const handleCustomerSelect = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchTerm(customer.name);
    setFetchingCustomer(true);
    try {
      // Fetch contract prices for this customer
      apiFetch(`${API_URL}/customers/${customer.id}/prices`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then((prices: any[]) => {
          const map: Record<string, number> = {};
          prices.forEach((p: any) => { map[String(p.product_id)] = parseFloat(p.price); });
          setContractPrices(map);
        })
        .catch(() => setContractPrices({}));

      const res = await apiFetch(`${API_URL}/customers/${customer.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const full = await res.json();
        const addr = [full.address, full.city, full.postcode, full.country].filter(Boolean).join(', ');
        setCreditBalance(toN(full.credit_balance));
        setCreditLimit(toN(full.credit_limit));
        setForm(f => ({
          ...f, customerEmail: full.email || '', customerPhone: full.phone || '',
          billingAddress: addr, shippingAddress: f.sameAsBilling ? addr : f.shippingAddress,
        }));
        setSelectedCustomer({ ...customer, credit_balance: toN(full.credit_balance), credit_terms: toN(full.credit_terms, 30), customer_type: full.customer_type || full.customerType || customer.customer_type || 'Retail', pricing_tier: full.pricing_tier || full.pricingTier || customer.pricing_tier || null });
      }
    } catch { /* use basic info */ }
    finally { setFetchingCustomer(false); }
  };

  /* ── item management (inline, like InvoiceForm) ── */
  const handleProductSelect = (key: string, productId: string) => {
    const p = products.find(p => String(p.id) === productId);
    if (!p) return;
    const isWholesale = selectedCustomer?.customer_type === 'Wholesale';
    const tier = (selectedCustomer?.pricing_tier || null) as PricingTier | null;
    const contractPrice = contractPrices[String(p.id)];
    const unitPrice = resolveTradeUnitPrice(p, { pricingTier: tier, isWholesale, contractPrice });
    const moq = p.min_order_qty || 1;
    if (contractPrice != null) {
      showToast(`📋 Contract price applied: £${contractPrice.toFixed(2)} for ${p.name}`);
    } else if (tier && unitPrice !== p.price) {
      showToast(`🏷️ Tier ${tier} price applied: £${unitPrice.toFixed(2)}`);
    }
    setItems(prev => prev.map(it =>
      it._key === key ? { ...it, productId: String(p.id), productName: p.name, price: unitPrice, unit: p.unit || 'pcs', stock: p.stock, saleType: 'unit', box_quantity: getCaseQuantity(p) || p.box_quantity, qty: Math.max(it.qty, moq) } : it
    ));
    if (p.min_order_qty && p.min_order_qty > 1) {
      showToast(`ℹ️ MOQ for ${p.name}: minimum ${p.min_order_qty} units`);
    }
  };

  const handleSaleTypeChange = (key: string, saleType: 'unit' | 'box') => {
    setItems(prev => prev.map(it => {
      if (it._key !== key) return it;
      const p = products.find(prod => String(prod.id) === it.productId);
      if (!p) return { ...it, saleType };
      const isWholesale = selectedCustomer?.customer_type === 'Wholesale';
      const tier = (selectedCustomer?.pricing_tier || null) as PricingTier | null;
      const contractPrice = contractPrices[String(p.id)] ?? null;
      const unitPrice = resolveTradeUnitPrice(p, { pricingTier: tier, isWholesale, contractPrice });
      if (saleType === 'box') {
        const bq = getCaseQuantity(p) || p.box_quantity || 0;
        if (!bq) return it;
        const boxPrice = resolveCasePrice(p, unitPrice, { pricingTier: tier, isWholesale, contractPrice }) ?? unitPrice * bq;
        return { ...it, saleType, price: boxPrice, unit: 'box', box_quantity: bq };
      }
      return { ...it, saleType, price: unitPrice, unit: p.unit || 'pcs', box_quantity: getCaseQuantity(p) || p.box_quantity };
    }));
  };

  const handleItemChange = (key: string, field: keyof OrderItem, value: any) => {
    setItems(prev => prev.map(it => it._key === key ? { ...it, [field]: value } : it));
  };

  const addItem = () => setItems(prev => [...prev, newItem()]);
  const removeItem = (key: string) => { if (items.length > 1) setItems(prev => prev.filter(it => it._key !== key)); };

  /* ── convert to invoice ── */
  const convertToInvoice = async () => {
    if (!id) { showToast('Save the order first before converting to invoice'); return; }
    if (!window.confirm('Generate an invoice from this order? If one already exists, you will be taken to it.')) return;

    setConverting(true);
    try {
      const res = await apiFetch(`${API_URL}/orders/${id}/generate-invoice`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const inv = data.invoice || data;
        const newId = inv.id || inv._id;
        if (data.existing) showToast('ℹ️ Invoice already exists — opening it');
        else showToast('✅ Invoice created successfully!');
        navigate(newId ? `/invoices/view/${newId}` : '/invoices/list');
      } else {
        const err = await res.json().catch(() => ({}));
        showToast(`❌ Failed to create invoice: ${err.message || err.error || 'Unknown error'}`);
      }
    } catch { showToast('❌ Network error'); }
    finally { setConverting(false); }
  };

  /* ── submit ── */
  const submit = async (e: React.FormEvent, mode: 'draft' | 'confirm') => {
    e.preventDefault();
    const customerName = isGuest ? searchTerm : (selectedCustomer?.name || '');
    if (!customerName) { showToast('Please select or enter a customer name'); return; }
    const validItems = items.filter(it => it.productName && it.qty > 0);
    if (validItems.length === 0) { showToast('Please add at least one item with a product selected'); return; }

    // MOQ + box multiple validation
    for (const it of validItems) {
      const prod = products.find(p => String(p.id) === it.productId);
      if (prod?.min_order_qty && it.qty < prod.min_order_qty) {
        showToast(`❌ ${it.productName}: minimum order qty is ${prod.min_order_qty} (you entered ${it.qty})`);
        return;
      }
      if (it.saleType === 'box' && prod?.box_quantity && it.qty % prod.box_quantity !== 0) {
        showToast(`❌ ${it.productName}: box orders must be in multiples of ${prod.box_quantity} (you entered ${it.qty})`);
        return;
      }
    }

    // Backorder check — warn if any item exceeds available stock
    const backorderedItems = validItems.filter(it => it.stock >= 0 && it.qty > it.stock);
    if (backorderedItems.length > 0 && !isEdit) {
      const names = backorderedItems.map(it => `${it.productName} (need ${it.qty}, have ${it.stock})`).join('\n');
      const proceed = window.confirm(
        `⚠️ The following items will be backordered (insufficient stock):\n\n${names}\n\nProceed? The order will be flagged as containing backordered items.`
      );
      if (!proceed) return;
    }

    const payload = {
      orderNumber: form.orderNumber, order_number: form.orderNumber,
      customerId: isGuest ? null : selectedCustomer?.id, customer_id: isGuest ? null : selectedCustomer?.id,
      customerName, customer_name: customerName,
      customerEmail: form.customerEmail, customer_email: form.customerEmail,
      customerPhone: form.customerPhone, customer_phone: form.customerPhone,
      billingAddress: form.billingAddress, billing_address: form.billingAddress,
      shippingAddress: form.sameAsBilling ? form.billingAddress : form.shippingAddress,
      shipping_address: form.sameAsBilling ? form.billingAddress : form.shippingAddress,
      orderDate: form.orderDate, order_date: form.orderDate,
      orderSource: form.orderSource, order_source: form.orderSource,
      status: mode === 'draft' ? 'Draft' : form.status,
      fulfillmentStatus: form.fulfillmentStatus, fulfillment_status: form.fulfillmentStatus,
      priority: form.priority,
      deliveryMethod: form.deliveryMethod, delivery_method: form.deliveryMethod,
      deliveryDate: form.deliveryDate || null, delivery_date: form.deliveryDate || null,
      trackingNumber: form.trackingNumber, tracking_number: form.trackingNumber,
      assignedStaff: form.assignedStaff, assigned_staff: form.assignedStaff,
      internalNotes: form.internalNotes, internal_notes: form.internalNotes,
      notes: form.notes, description: form.notes,
      shippingCost: shipping, shipping_cost: shipping,
      taxRate: toN(form.taxRate), tax_rate: toN(form.taxRate),
      discountType: form.discountType, discount_type: form.discountType,
      discountValue: toN(form.discountValue), discount_value: toN(form.discountValue),
      subtotal: lineSubtotal, orderDiscount, vat, total,
      depositAmount: depositAmount || null, deposit_amount: depositAmount || null,
      items: validItems.map(it => ({
        productId: it.productId || null, product_id: it.productId || null,
        productName: it.productName, product_name: it.productName,
        description: it.description, unit: it.unit,
        qty: it.qty, quantity: it.qty,
        price: it.price, unit_price: it.price,
        discount: it.discount || 0, discount_pct: it.discount || 0,
        lineTotal: it.qty * it.price * (1 - (it.discount || 0) / 100),
        saleType: it.saleType, sale_type: it.saleType,
        box_quantity: it.box_quantity ?? null,
      })),
    };

    try {
      setSaving(true);
      const res = await apiFetch(isEdit ? `${API_URL}/orders/${id}` : `${API_URL}/orders`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        showToast(`✅ Order ${isEdit ? 'updated' : mode === 'draft' ? 'saved as Draft' : 'created'} successfully!`);
        navigate('/orders/list');
      } else if (res.status === 422) {
        const err = await res.json().catch(() => ({}));
        if (err.creditLimitExceeded) {
          const override = window.confirm(
            `⚠️ Credit Limit Exceeded\n\n${err.message}\n\nProceed anyway? (Manager override)`
          );
          if (override) {
            const res2 = await apiFetch(isEdit ? `${API_URL}/orders/${id}` : `${API_URL}/orders`, {
              method: isEdit ? 'PUT' : 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ ...payload, bypassCreditCheck: true }),
            });
            if (res2.ok) {
              showToast('✅ Order created (credit limit override applied)');
              navigate('/orders/list');
            } else {
              const e2 = await res2.json().catch(() => ({}));
              showToast(`❌ Failed: ${e2.message || 'Unknown error'}`);
            }
          }
        } else {
          showToast(`❌ ${err.message || 'Validation error'}`);
        }
      } else if (res.status === 423) {
        const err = await res.json().catch(() => ({}));
        showToast(`🔒 ${err.message || 'Order amounts are locked — edit the invoice instead'}`);
      } else {
        const err = await res.json().catch(() => ({}));
        showToast(`❌ Failed: ${err.message || 'Unknown error'}`);
      }
    } catch { showToast('❌ Network error'); }
    finally { setSaving(false); }
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canConvert = isEdit && ['Confirmed', 'Processing', 'Shipped', 'Completed'].includes(form.status);
  const financialLocked = isEdit && !billingContext.canEditOrderFinancials;
  const lockStyle: React.CSSProperties = financialLocked
    ? { opacity: 0.55, pointerEvents: 'none', userSelect: 'none' as const }
    : {};

  /* ══════════════ RENDER ═════════════════ */
  if (pageLoading) return <div style={{ padding: 60, textAlign: 'center', fontSize: 18 }}>⏳ Loading Order...</div>;

  return (
    <>
      <style>{`
        .of-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0 14px; }
        .of-grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0 14px; }
        .of-act { display: flex; gap: 12px; }
        @media(max-width:640px) {
          .of-grid2, .of-grid3 { grid-template-columns: 1fr !important; }
          .of-act { flex-direction: column; }
          .of-act button { width: 100% !important; }
          .of-2col { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <form onSubmit={e => e.preventDefault()} style={st.form}>

        {/* ── Header ── */}
        <div style={st.header}>
          <div>
            <h2 style={st.title}>
              {isEdit
                ? (financialLocked ? '📦 Order fulfilment' : '✏️ Edit Order')
                : '🛒 New Order'}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
              <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: STATUS_COLORS[form.status] + '22', color: STATUS_COLORS[form.status], border: `1.5px solid ${STATUS_COLORS[form.status]}` }}>{form.status}</span>
              <span style={{ fontSize: 13, color: '#9ca3af' }}>{form.orderNumber}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {isEdit && <button type="button" onClick={() => navigate(`/orders/view/${id}`)} style={st.hBtn}>👁 View</button>}
            <button type="button" onClick={() => navigate('/orders/list')} style={st.hBtn}>← Back</button>
          </div>
        </div>

        {isEdit && <OrderBillingBanner billing={billingContext} />}

        {/* ── CARD 1: Customer ── */}
        <div style={{ ...st.card, ...lockStyle }}>
          <div style={st.cardTitle}>👤 Customer</div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <button type="button" onClick={() => setIsGuest(false)} style={{ ...st.pill, ...(isGuest ? st.pillOff : st.pillOn) }}>Existing Customer</button>
            <button type="button" onClick={() => setIsGuest(true)} style={{ ...st.pill, ...(!isGuest ? st.pillOff : st.pillOn) }}>Guest / Walk-in</button>
            <button type="button" onClick={() => navigate('/customers/new')} style={st.pillGreen}>+ New Customer</button>
          </div>

          {!isGuest ? (
            <div style={{ position: 'relative' }}>
              <input type="text" value={searchTerm} placeholder="Search customer by name..."
                onChange={e => { setSearchTerm(e.target.value); setSelectedCustomer(null); }}
                style={st.input} />
              {searchTerm && !selectedCustomer && filteredCustomers.length > 0 && (
                <div style={st.dropdown}>
                  {filteredCustomers.slice(0, 8).map(c => (
                    <div key={c.id} style={st.dropItem} onClick={() => handleCustomerSelect(c)}>
                      <div style={{ fontWeight: 600 }}>{c.name}
                        {c.customer_type === 'Wholesale' && <span style={{ marginLeft: 8, fontSize: 11, background: '#eff6ff', color: '#3b82f6', padding: '1px 6px', borderRadius: 10, fontWeight: 700 }}>Wholesale</span>}
                      </div>
                      {c.companyName && <div style={{ fontSize: 12, color: '#6b7280' }}>{c.companyName}</div>}
                      {c.email && <div style={{ fontSize: 12, color: '#9ca3af' }}>{c.email}</div>}
                    </div>
                  ))}
                </div>
              )}
              {fetchingCustomer && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Loading...</div>}
              {selectedCustomer && !fetchingCustomer && (
                <div style={st.badge}>
                  ✓ {selectedCustomer.name}{selectedCustomer.companyName ? ` — ${selectedCustomer.companyName}` : ''}
                  {selectedCustomer.customer_type === 'Wholesale' && <span style={{ marginLeft: 8, fontSize: 11, background: '#eff6ff', color: '#3b82f6', padding: '1px 8px', borderRadius: 10, fontWeight: 700 }}>📦 Wholesale — auto wholesale prices applied</span>}
                  <button type="button" onClick={() => { setSelectedCustomer(null); setSearchTerm(''); }} style={{ marginLeft: 12, background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700 }}>✕</button>
                </div>
              )}
            </div>
          ) : (
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Guest name" style={st.input} />
          )}

          {creditBalance > 0 && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', marginTop: 8, fontSize: 13, color: '#166534' }}>
              💳 <strong>£{creditBalance.toFixed(2)}</strong> credit available
              {creditLimit > 0 && `  |  Limit: £${creditLimit.toFixed(2)}`}
            </div>
          )}
          {selectedCustomer && creditLimit > 0 && total > 0 && total > (creditLimit - creditBalance) && (
            <div style={{ background: '#fef2f2', border: '2px solid #ef4444', borderRadius: 8, padding: '10px 14px', marginTop: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 20 }}>🚨</span>
              <div>
                <div style={{ fontWeight: 700, color: '#b91c1c', fontSize: 13 }}>Credit Limit Warning</div>
                <div style={{ fontSize: 12, color: '#dc2626', marginTop: 2 }}>
                  Order £{total.toFixed(2)} exceeds available credit £{Math.max(0, creditLimit - creditBalance).toFixed(2)} (Limit: £{creditLimit.toFixed(2)}). You will be asked to confirm on save.
                </div>
              </div>
            </div>
          )}

          <div className="of-grid2" style={{ marginTop: 12 }}>
            <div><label style={st.lbl}>Email</label><input value={form.customerEmail} onChange={e => setForm(f => ({ ...f, customerEmail: e.target.value }))} style={st.input} placeholder="customer@example.com" /></div>
            <div><label style={st.lbl}>Phone</label><input value={form.customerPhone} onChange={e => setForm(f => ({ ...f, customerPhone: e.target.value }))} style={st.input} placeholder="+44..." /></div>
          </div>
          <div className="of-grid2">
            <div>
              <label style={st.lbl}>Billing Address</label>
              <textarea value={form.billingAddress} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, billingAddress: v, shippingAddress: f.sameAsBilling ? v : f.shippingAddress })); }} style={{ ...st.input, height: 60, resize: 'vertical' as const }} placeholder="Street, City, Postcode" />
            </div>
            <div>
              <label style={st.lbl}>Shipping Address</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', marginBottom: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.sameAsBilling} onChange={e => setForm(f => ({ ...f, sameAsBilling: e.target.checked, shippingAddress: e.target.checked ? f.billingAddress : f.shippingAddress }))} />
                Same as billing
              </label>
              {!form.sameAsBilling && <textarea value={form.shippingAddress} onChange={e => setForm(f => ({ ...f, shippingAddress: e.target.value }))} style={{ ...st.input, height: 60, resize: 'vertical' as const }} placeholder="Street, City, Postcode" />}
            </div>
          </div>
        </div>

        {/* ── CARD 2: Order Items ── */}
        <div style={{ ...st.card, ...lockStyle }}>
          <div style={st.cardTitle}>📦 Order Items</div>
          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 12 }}>
            <table style={{ width: '100%', minWidth: 680, borderCollapse: 'collapse', background: '#fff' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={st.th}>Product</th>
                  <th style={{ ...st.th, width: 60, textAlign: 'center' as const }}>Qty</th>
                  <th style={{ ...st.th, width: 100, textAlign: 'right' as const }}>Price £</th>
                  <th style={{ ...st.th, width: 70, textAlign: 'center' as const }}>Disc %</th>
                  <th style={{ ...st.th, width: 100, textAlign: 'right' as const }}>Total £</th>
                  <th style={{ ...st.th, width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const gross = item.qty * item.price;
                  const lineTotal = gross * (1 - (item.discount || 0) / 100);
                  const effectiveStock = item.saleType === 'box' && item.box_quantity
                    ? Math.floor(item.stock / item.box_quantity)
                    : item.stock;
                  const lowStock = effectiveStock >= 0 && effectiveStock < 5;
                  const outOfStock = effectiveStock === 0;
                  return (
                    <tr key={item._key} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={st.td}>
                        <select value={item.productId || ''} onChange={e => handleProductSelect(item._key, e.target.value)} style={st.sel}>
                          <option value="">— Select product —</option>
                          {products.map(p => (
                            <option key={p.id} value={String(p.id)}>
                              {p.name} — {selectedCustomer?.customer_type === 'Wholesale' && p.wholesale_price != null && p.wholesale_price > 0 ? `£${p.wholesale_price.toFixed(2)} (W)` : `£${p.price.toFixed(2)}`}
                            </option>
                          ))}
                        </select>
                        {item.productId && item.box_quantity && item.box_quantity > 0 && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                            <button type="button" onClick={() => handleSaleTypeChange(item._key, 'unit')}
                              style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, border: '1.5px solid #3b82f6', background: item.saleType === 'unit' ? '#3b82f6' : 'transparent', color: item.saleType === 'unit' ? '#fff' : '#3b82f6', cursor: 'pointer', fontWeight: 600 }}>
                              Unit
                            </button>
                            <button type="button" onClick={() => handleSaleTypeChange(item._key, 'box')}
                              style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, border: '1.5px solid #8b5cf6', background: item.saleType === 'box' ? '#8b5cf6' : 'transparent', color: item.saleType === 'box' ? '#fff' : '#8b5cf6', cursor: 'pointer', fontWeight: 600 }}>
                              📦 Box ({item.box_quantity} units)
                            </button>
                          </div>
                        )}
                        {item.productId && item.stock >= 0 && (
                          <div style={{ fontSize: 11, marginTop: 2, color: outOfStock ? '#ef4444' : lowStock ? '#f59e0b' : '#10b981', fontWeight: 600 }}>
                            {item.saleType === 'box' && item.box_quantity
                              ? (item.stock === 0 ? '❌ Out of stock' : `📦 ${effectiveStock} boxes available`)
                              : (outOfStock ? '❌ Out of stock' : lowStock ? `⚠ ${item.stock} left` : `✓ ${item.stock} in stock`)}
                          </div>
                        )}
                        <input type="text" value={item.description} onChange={e => handleItemChange(item._key, 'description', e.target.value)} style={{ ...st.inputSm, marginTop: 4, fontSize: 12, color: '#6b7280' }} placeholder="Note (optional)" />
                      </td>
                      <td style={{ ...st.td, textAlign: 'center' as const }}>
                        <input type="number" value={item.qty} min={1} onChange={e => handleItemChange(item._key, 'qty', Number(e.target.value))} style={{ ...st.inputSm, width: 54, textAlign: 'center' as const }} />
                      </td>
                      <td style={{ ...st.td, textAlign: 'right' as const }}>
                        <input type="number" value={item.price} min={0} step={0.01} onChange={e => handleItemChange(item._key, 'price', Number(e.target.value))} style={{ ...st.inputSm, width: 80, textAlign: 'right' as const }} />
                      </td>
                      <td style={{ ...st.td, textAlign: 'center' as const }}>
                        <input type="number" value={item.discount} min={0} max={100} step={0.5} onChange={e => handleItemChange(item._key, 'discount', Number(e.target.value))} style={{ ...st.inputSm, width: 54, textAlign: 'center' as const }} />
                      </td>
                      <td style={{ ...st.td, textAlign: 'right' as const }}>
                        {item.discount > 0 && <div style={{ fontSize: 11, color: '#9ca3af', textDecoration: 'line-through' }}>£{gross.toFixed(2)}</div>}
                        <strong style={{ color: '#10b981' }}>£{lineTotal.toFixed(2)}</strong>
                      </td>
                      <td style={{ ...st.td, textAlign: 'center' as const }}>
                        {items.length > 1 && <button type="button" onClick={() => removeItem(item._key)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✖</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={addItem} style={st.addBtn}>＋ Add Item</button>
        </div>

        {/* ── CARD 3: Totals & Charges ── */}
        <div style={{ ...st.card, ...lockStyle }}>
          <div style={st.cardTitle}>💰 Totals</div>
          <div className="of-grid3">
            <div>
              <label style={st.lbl}>Order Discount</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={form.discountType} onChange={e => setForm(f => ({ ...f, discountType: e.target.value as 'percentage' | 'fixed' }))} style={{ ...st.input, width: 80, marginBottom: 0, flexShrink: 0 }}>
                  <option value="percentage">% Off</option>
                  <option value="fixed">£ Off</option>
                </select>
                <input type="number" value={form.discountValue} min={0} step={0.01} onChange={e => setForm(f => ({ ...f, discountValue: e.target.value }))} style={{ ...st.input, marginBottom: 0 }} />
              </div>
            </div>
            <div><label style={st.lbl}>VAT / Tax (%)</label><input type="number" value={form.taxRate} min={0} step={0.01} onChange={e => setForm(f => ({ ...f, taxRate: e.target.value }))} style={st.input} /></div>
            <div><label style={st.lbl}>Shipping £</label><input type="number" value={form.shippingCost} min={0} step={0.01} onChange={e => setForm(f => ({ ...f, shippingCost: e.target.value }))} style={st.input} /></div>
          </div>
          {/* Deposit amount input */}
          <div style={{ marginBottom: 14 }}>
            <label style={st.lbl}>💰 Deposit / Part Payment (£) <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12 }}>— leave 0 if full payment on invoice</span></label>
            <input
              type="number" min={0} step={0.01}
              value={depositAmount || ''}
              onChange={e => setDepositAmount(parseFloat(e.target.value) || 0)}
              style={{ ...st.input, maxWidth: 200 }}
              placeholder="0.00"
            />
          </div>

          <div style={st.totals}>
            <div style={st.tRow}><span>Subtotal</span><span>£{lineSubtotal.toFixed(2)}</span></div>
            {orderDiscount > 0 && <div style={{ ...st.tRow, color: '#ef4444' }}><span>Discount</span><span>-£{orderDiscount.toFixed(2)}</span></div>}
            <div style={st.tRow}><span>VAT ({form.taxRate}%)</span><span>£{vat.toFixed(2)}</span></div>
            {shipping > 0 && <div style={st.tRow}><span>Shipping</span><span>£{shipping.toFixed(2)}</span></div>}
            <div style={st.tFinal}><span>Total</span><span>£{total.toFixed(2)}</span></div>
            {depositAmount > 0 && (
              <div style={{ ...st.tRow, color: '#10b981', fontWeight: 600 }}>
                <span>{depositConfirmed ? '✅ Deposit Confirmed' : '💰 Deposit'}</span>
                <span>-£{depositAmount.toFixed(2)}</span>
              </div>
            )}
            {depositAmount > 0 && (
              <div style={{ ...st.tRow, fontWeight: 700, color: '#f59e0b' }}>
                <span>⏳ Remaining Balance</span>
                <span>£{Math.max(0, total - depositAmount).toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── CARD 4: Status & Dates ── */}
        <div style={st.card}>
          <div style={st.cardTitle}>📋 Status & Dates</div>
          <div className="of-grid2" style={{ marginBottom: 16 }}>
            <div><label style={st.lbl}>Order Number</label><input value={form.orderNumber} readOnly={financialLocked} onChange={e => setForm(f => ({ ...f, orderNumber: e.target.value }))} style={st.input} /></div>
            <div><label style={st.lbl}>Order Date</label><input type="date" value={form.orderDate} onChange={e => setForm(f => ({ ...f, orderDate: e.target.value }))} style={st.input} /></div>
          </div>
          <div className="of-grid2" style={{ marginBottom: 16 }}>
            <div>
              <label style={st.lbl}>Source</label>
              <select value={form.orderSource} onChange={e => setForm(f => ({ ...f, orderSource: e.target.value }))} style={st.input}>
                {ORDER_SOURCES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div><label style={st.lbl}>Delivery Date</label><input type="date" value={form.deliveryDate} onChange={e => setForm(f => ({ ...f, deliveryDate: e.target.value }))} style={st.input} /></div>
          </div>

          <label style={st.lbl}>Order Status</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {ORDER_STATUSES.map(s => (
              <button key={s} type="button" onClick={() => setForm(f => ({ ...f, status: s }))}
                style={{ padding: '7px 16px', borderRadius: 20, border: `1.5px solid ${STATUS_COLORS[s]}`, background: form.status === s ? STATUS_COLORS[s] : 'transparent', color: form.status === s ? '#fff' : STATUS_COLORS[s], fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{s}</button>
            ))}
          </div>

          <label style={st.lbl}>Fulfillment</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {FULFILLMENT_STATUSES.map(s => (
              <button key={s} type="button" onClick={() => setForm(f => ({ ...f, fulfillmentStatus: s }))}
                style={{ padding: '7px 16px', borderRadius: 20, border: `1.5px solid ${FULFIL_COLORS[s]}`, background: form.fulfillmentStatus === s ? FULFIL_COLORS[s] : 'transparent', color: form.fulfillmentStatus === s ? '#fff' : FULFIL_COLORS[s], fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{s}</button>
            ))}
          </div>

          <div className="of-grid2">
            <div>
              <label style={st.lbl}>Delivery Method</label>
              <select value={form.deliveryMethod} onChange={e => setForm(f => ({ ...f, deliveryMethod: e.target.value }))} style={st.input}>
                <option>Pickup</option><option>Standard</option><option>Express</option><option>Next Day</option><option>Freight</option>
              </select>
            </div>
            <div><label style={st.lbl}>Tracking Number</label><input value={form.trackingNumber} onChange={e => setForm(f => ({ ...f, trackingNumber: e.target.value }))} style={st.input} placeholder="JD000..." /></div>
          </div>
        </div>

        {/* ── CARD 5: Priority & Notes ── */}
        <div style={st.card}>
          <div style={st.cardTitle}>🔒 Priority & Notes</div>
          <div className="of-grid2" style={{ marginBottom: 12 }}>
            <div>
              <label style={st.lbl}>Assigned Staff</label>
              <input value={form.assignedStaff} onChange={e => setForm(f => ({ ...f, assignedStaff: e.target.value }))} style={st.input} placeholder="Staff member" />
            </div>
            <div>
              <label style={st.lbl}>Priority</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {PRIORITY_CONFIG.map(p => (
                  <button key={p.value} type="button" onClick={() => setForm(f => ({ ...f, priority: p.value }))}
                    style={{ flex: 1, padding: '9px 4px', borderRadius: 8, border: `1.5px solid ${form.priority === p.value ? p.color : '#e5e7eb'}`, background: form.priority === p.value ? p.color + '22' : '#fff', color: form.priority === p.value ? p.color : '#6b7280', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                    {p.icon} {p.value}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <label style={st.lbl}>Notes <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>(visible to customer)</span></label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ ...st.input, height: 64, resize: 'vertical' as const, marginBottom: 12 }} placeholder="Order notes..." />
          <label style={st.lbl}>Internal Notes <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>(staff only)</span></label>
          <textarea value={form.internalNotes} onChange={e => setForm(f => ({ ...f, internalNotes: e.target.value }))} style={{ ...st.input, height: 64, resize: 'vertical' as const }} placeholder="Private notes..." />
        </div>

        {/* ── Convert to Invoice ── */}
        {canConvert && !billingContext.hasInvoice && (
          <div style={{ marginBottom: 20, padding: '16px 22px', background: '#eff6ff', border: '2px solid #3b82f6', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, color: '#1d4ed8', fontSize: 15 }}>🧾 Ready to Invoice?</div>
              <div style={{ color: '#3b82f6', fontSize: 13, marginTop: 2 }}>Order is {form.status} — convert to invoice now.</div>
            </div>
            <button type="button" onClick={convertToInvoice} disabled={converting}
              style={{ padding: '11px 24px', background: converting ? '#9ca3af' : '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, cursor: converting ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14 }}>
              {converting ? '⏳ Creating...' : '🧾 Convert to Invoice'}
            </button>
          </div>
        )}

        {/* ── Actions ── */}
        <div className="of-act">
          {billingContext.canEditInvoiceLines && billingContext.invoiceId && (
            <button type="button" onClick={() => navigate(`/invoices/edit/${billingContext.invoiceId}`)}
              style={{ padding: '13px 24px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
              🧾 Edit Invoice
            </button>
          )}
          <button type="button" onClick={() => navigate('/orders/list')}
            style={{ padding: '13px 24px', background: '#fff', border: '1.5px solid #d1d5db', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 15, color: '#6b7280' }}>
            Cancel
          </button>
          {!financialLocked && (
            <button type="button" disabled={saving} onClick={e => submit(e as any, 'draft')}
              style={{ padding: '13px 24px', background: saving ? '#9ca3af' : '#f3f4f6', border: '1.5px solid #6b7280', borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 15, color: '#374151' }}>
              {saving ? '⏳ Saving...' : '💾 Save Draft'}
            </button>
          )}
          <button type="button" disabled={saving} onClick={e => submit(e as any, 'confirm')}
            style={{ flex: 1, padding: '13px 24px', background: saving ? '#9ca3af' : '#10b981', color: '#fff', border: 'none', borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 15, boxShadow: saving ? 'none' : '0 4px 12px rgba(16,185,129,0.3)' }}>
            {saving ? '⏳ Saving...' : financialLocked ? '✅ Save fulfilment' : (isEdit ? '✅ Update Order' : '✅ Create Order')}
          </button>
        </div>

      </form>
    </>
  );
};

/* ─── Styles ─────────────────────────────────────────── */
const st: any = {
  form: { maxWidth: 860, margin: '28px auto', padding: '0 16px 40px', fontFamily: 'system-ui,-apple-system,sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, paddingBottom: 16, borderBottom: '2px solid #10b981' },
  title: { margin: 0, fontSize: 24, fontWeight: 700, color: '#1f2937' },
  hBtn: { padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#374151' },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 22px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#374151', marginBottom: 16, paddingBottom: 10, borderBottom: '1.5px solid #f3f4f6' },
  lbl: { display: 'block', fontWeight: 600, marginBottom: 5, fontSize: 13, color: '#374151' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', marginBottom: 14, fontSize: 14, boxSizing: 'border-box' as const, outline: 'none' },
  pill: { padding: '9px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  pillOn: { background: '#3b82f6', color: '#fff' },
  pillOff: { background: '#f3f4f6', color: '#6b7280', border: '1.5px solid #e5e7eb' },
  pillGreen: { padding: '9px 16px', borderRadius: 8, border: '1.5px solid #10b981', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: '#ecfdf5', color: '#059669' },
  dropdown: { position: 'absolute' as const, top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', zIndex: 1000, maxHeight: 260, overflowY: 'auto' as const },
  dropItem: { padding: '11px 14px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' },
  badge: { padding: '10px 14px', background: '#d1fae5', borderRadius: 8, border: '1.5px solid #10b981', marginBottom: 12, color: '#059669', fontWeight: 600, fontSize: 14 },
  th: { padding: '10px 12px', textAlign: 'left' as const, fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, borderBottom: '1.5px solid #e5e7eb', background: '#f9fafb' },
  td: { padding: '8px 10px', verticalAlign: 'top' as const },
  sel: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1.5px solid #e5e7eb', fontSize: 13, background: '#fff' },
  inputSm: { width: '100%', padding: '7px 9px', borderRadius: 6, border: '1.5px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box' as const },
  addBtn: { padding: '9px 20px', borderRadius: 8, border: '1.5px dashed #10b981', background: '#f0fdf4', color: '#059669', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  totals: { marginTop: 12, padding: '14px 18px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #86efac', maxWidth: 340, marginLeft: 'auto' },
  tRow: { display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, color: '#6b7280' },
  tFinal: { display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: '1.5px solid #10b981', fontSize: 18, fontWeight: 700, color: '#1f2937' },
};

export default OrderForm;
